'use client'

import { useState, useEffect } from 'react'
import Link from 'next/link'
// Replaced lucide-react icons with emoji to avoid Runtime ReferenceError
import CustomDropdown from '@/components/CustomDropdown'
import UserRankBadge from '@/components/UserRankBadge'
import MathLeetLogo from '@/components/MathLeetLogo'

interface Discussion {
  id: string
  title: string
  content: string
  problem?: {
    title: string
    slug: string
    category: string
  }
  user: {
    username: string
    name: string
    rank: string
    userRank: string
  }
  replies: number
  likes: number
  hasLiked: boolean
  isPinned: boolean
  isHelpQuestion: boolean
  hasSolution: boolean
  solutionCount: number
  createdAt: string
}

export default function DiscussPage() {
  const [discussions, setDiscussions] = useState<Discussion[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [filter, setFilter] = useState<'all' | 'general' | 'problems'>('all')
  const [typeFilter, setTypeFilter] = useState<'all' | 'discussion' | 'help'>('all')

  useEffect(() => {
    fetchDiscussions()
  }, [filter, typeFilter])

  const fetchDiscussions = async () => {
    setIsLoading(true)
    try {
      const response = await fetch(`/api/discussions?filter=${filter}&type=${typeFilter}`)
      const data = await response.json()
      if (response.ok) {
        setDiscussions(data)
      }
    } catch (error) {
      console.error('Error fetching discussions:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const getRankColor = (rank: string) => {
    switch (rank) {
      case 'Candidate Master': return 'rating-candidate-master'
      case 'Expert': return 'rating-expert'  
      case 'Specialist': return 'rating-specialist'
      case 'Pupil': return 'rating-pupil'
      default: return 'rating-newbie'
    }
  }

  const getCategoryColor = (category: string) => {
    const colors = {
      'AIME': 'category-aime',
      'AMC': 'category-amc', 
      'MathCounts': 'category-mathcounts',
      'IOI': 'category-ioi',
      'General': 'category-general'
    }
    return colors[category as keyof typeof colors] || 'category-general'
  }

  if (isLoading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="animate-pulse">
          <div className="bg-dark-700 h-8 w-64 rounded mb-8"></div>
          <div className="space-y-4">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="bg-dark-700 h-24 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-white mb-4">Discussions</h1>
        <p className="text-gray-200 mb-6">
          Join conversations about mathematics, problem-solving techniques, and competition strategies
        </p>

        <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4 mb-6">
          <div className="flex items-center gap-4">
            <div className="min-w-[250px]">
              <CustomDropdown
                options={[
                  { value: 'all', label: 'All Discussions', description: 'Show all discussion types', icon: <span className="inline-block w-4 h-4 text-center">🔍</span> },
                  { value: 'general', label: 'General Math', description: 'General mathematical discussions', icon: <span className="inline-block w-4 h-4 text-center">📖</span> },
                  { value: 'problems', label: 'Problem Discussions', description: 'Discussions about specific problems', icon: <MathLeetLogo size="sm" /> },
                ]}
                value={filter}
                onChange={setFilter}
                placeholder="Select discussion type"
              />
            </div>
            <div className="min-w-[200px]">
              <CustomDropdown
                options={[
                  { value: 'all', label: 'All Posts', description: 'Show both discussions and help questions', icon: <span className="inline-block w-4 h-4 text-center">🔍</span> },
                  { value: 'discussion', label: 'Discussions', description: 'Regular discussions only', icon: <span className="inline-block w-4 h-4 text-center">💬</span> },
                  { value: 'help', label: 'Help Questions', description: 'Help questions only', icon: <span className="inline-block w-4 h-4 text-center">❓</span> },
                ]}
                value={typeFilter}
                onChange={setTypeFilter}
                placeholder="Select post type"
              />
            </div>
          </div>
          
          <Link
            href="/discuss/new"
            className="btn btn-primary flex items-center gap-2 whitespace-nowrap"
          >
            <span className="inline-block w-4 h-4 text-center">➕</span>
            New Discussion
          </Link>
        </div>
      </div>

      {discussions.length === 0 ? (
        <div className="text-center py-12">
          <span className="text-xl">💬</span>
          <p className="text-gray-200 text-lg">No discussions found</p>
          <p className="text-gray-300">Be the first to start a conversation!</p>
        </div>
      ) : (
        <div className="space-y-4">
          {discussions.map((discussion) => (
            <div key={discussion.id} className={`card p-6 ${discussion.isPinned ? 'ring-2 ring-yellow-400/50 bg-yellow-900/10' : ''}`}>
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-2 mb-2">
                    {discussion.isPinned && (
                      <span className="inline-block w-4 h-4 text-center">📍</span>
                    )}
                    {discussion.isHelpQuestion && (
                      <div className="flex items-center gap-1 text-blue-400">
                        <span className="inline-block w-4 h-4 text-center">❓</span>
                        <span className="text-xs font-medium">HELP</span>
                      </div>
                    )}
                    {discussion.hasSolution && (
                      <div className="flex items-center gap-1 text-green-400">
                        <span className="inline-block w-4 h-4 text-center">✅</span>
                        <span className="text-xs font-medium">SOLVED</span>
                      </div>
                    )}
                    <h3 className="text-lg font-semibold text-white">
                      <Link
                        href={`/discuss/${discussion.id}`}
                        className="hover:text-primary-400 transition-colors duration-200"
                      >
                        {discussion.title}
                      </Link>
                    </h3>
                  </div>

                  <p className="text-gray-200 mb-3 line-clamp-2">
                    {discussion.content}
                  </p>

                  {discussion.problem && (
                    <div className="mb-3">
                      <Link
                        href={`/problems/${discussion.problem.slug}`}
                        className="inline-flex items-center space-x-2 text-sm"
                      >
                        <span className={`px-2 py-1 rounded-full ${getCategoryColor(discussion.problem.category)}`}>
                          {discussion.problem.category}
                        </span>
                        <span className="text-primary-400 hover:text-primary-300 font-medium">
                          {discussion.problem.title}
                        </span>
                      </Link>
                    </div>
                  )}

                  <div className="flex items-center justify-between text-sm">
                    <div className="flex items-center space-x-4">
                      <Link 
                        href={`/users/${discussion.user.username}`}
                        className="flex items-center space-x-2 hover:text-primary-400"
                      >
                        <span className="inline-block w-4 h-4 text-center">👤</span>
                        <span className="font-medium">
                          {discussion.user.name || discussion.user.username}
                        </span>
                        {discussion.user.userRank && discussion.user.userRank !== 'User' && (
                          <UserRankBadge rank={discussion.user.userRank} size="sm" showLabel={false} />
                        )}
                      </Link>
                      
                      <div className="flex items-center space-x-1 text-gray-300">
                        <span className="inline-block w-4 h-4 text-center">⏰</span>
                        <span>{new Date(discussion.createdAt).toLocaleDateString()}</span>
                      </div>
                    </div>

                    <div className="flex items-center space-x-4 text-gray-300">
                      <div className="flex items-center space-x-1">
                        <span className="inline-block w-4 h-4 text-center">💬</span>
                        <span>{discussion.replies} replies</span>
                        {discussion.isHelpQuestion && discussion.solutionCount > 0 && (
                          <span className="text-green-400 text-xs">({discussion.solutionCount} solution{discussion.solutionCount !== 1 ? 's' : ''})</span>
                        )}
                      </div>
                      <div className={`flex items-center space-x-1 ${discussion.hasLiked ? 'text-primary-400' : ''}`}>
                        <span className="inline-block w-4 h-4 text-center">👍</span>
                        <span>{discussion.likes}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="mt-12 grid md:grid-cols-3 gap-6">
        <div className="card p-6 text-center">
          <span className="text-xl">💬</span>
          <h3 className="text-lg font-semibold mb-2 text-white">Problem Help</h3>
          <p className="text-gray-200 text-sm">
            Get help with specific math problems from the community.
          </p>
        </div>
        
        <div className="card p-6 text-center">
          <span className="text-5xl text-green-400 mx-auto mb-4 block">👤</span>
          <h3 className="text-lg font-semibold mb-2 text-white">Study Groups</h3>
          <p className="text-gray-200 text-sm">
            Form study groups and collaborate on competition preparation.
          </p>
        </div>
        
        <div className="card p-6 text-center">
          <span className="text-5xl text-purple-400 mx-auto mb-4 block">👍</span>
          <h3 className="text-lg font-semibold mb-2 text-white">Share Solutions</h3>
          <p className="text-gray-200 text-sm">
            Share elegant solutions and learn from different approaches.
          </p>
        </div>
      </div>
    </div>
  )
}